import { computedFrom, autoinject, bindable } from 'aurelia-framework';
import { Router } from 'aurelia-router';
import { ContactInformation } from '../../models/contactInformation';
import { State } from '../../services/global-state';
import { PayrazrService } from '../../services/payrazr';
import { ValidationHelpers } from '../../services/validation-helpers';
import { I18N } from 'aurelia-i18n';
import { Customer } from '../../models/customer';
import { MfaTwoFactorMethod } from '../../models/mfa/mfa-two-factor-method';

@autoinject

export class Preferences {
	savedPhoneNumber: any;
	model: any;
	isSMSEnabled: boolean;
	errorMessage: any;
	validationErrors: any;
	smsRequiresVerification: any;
	isSmsOptedIn: boolean;
	isInvoiceEnabled: boolean;
	successItem: any;
	@bindable selectedCustomer: Customer;
	showMfaHelpfulTextForEmail: boolean;
	showMfaHelpfulTextForSMS: boolean;

	constructor(private router: Router, private payrazrService: PayrazrService, private globalState: State, private i18n: I18N) {
		this.savedPhoneNumber = '';  // This is the mobile telephone number for user currently persisted in Payrazr DB
		this.isSMSEnabled = false;
		this.errorMessage = '';
		this.validationErrors = [];
		this.smsRequiresVerification = false;
		this.isSmsOptedIn = false;
		this.successItem = '';
	}

	activate() {
		this.isInvoiceEnabled = this.globalState.business.portalSettings.businessSettings.isInvoiceEnabled;
		this.refreshGlobalCustomer();
	}

	selectedCustomerChanged() {
		this.refreshGlobalCustomer(this.selectedCustomer.id);
	}

	// Both this and avalableCustomerDescs are called multiple times by
	// the view for unsure reasons; if there's a notable performance lag
	// in the future when switching customers/patients, try debugging here.
	@computedFrom('globalState.customer', 'globalState.associatedCustomers')
	get availableCustomers(): Customer[] {
		return [this.globalState.customer].concat(this.globalState.associatedCustomers);
	}

	@computedFrom('availableCustomers')
	get availableCustomerDescs(): string[] {
		return this.availableCustomers.map(c => c.name);
	}

	refreshGlobalCustomer(customerId: string = null) {
		this.payrazrService.refreshGlobalCustomer()
			.then(response => {
				let customer = this.globalState.customer;

				if (customerId) {
					this.globalState.associatedCustomers.forEach(x => {
						if (x.id === customerId) {
							customer = x;
						}
					});
				}

				this.model = new Model(customer);
				this.isSmsOptedIn = customer.notificationSettings.smsIsOptedIn;
				this.smsRequiresVerification = !customer.notificationSettings.smsIsValidated;
				this.isSMSEnabled = this.globalState.business.portalSettings.businessSettings.enableSMS;
				// If savedPhoneNumber is valid, save for comparison against mobilePhone field
				let validator = new MobileNumberValidator(this.i18n);
				if (validator.validNumber(customer)) {
					this.savedPhoneNumber = ValidationHelpers.sanitizePhoneNumber(customer.mobilePhone);
				} else {
					this.savedPhoneNumber = 'X'; // can't be matched
				}
				this.setupMfaHelpfulText();
			})
			.catch(err => {
				console.error('Failed to refresh customer information.');
			});
	}

	setupMfaHelpfulText() {
		if (this.globalState.features.mfaConsumerPortal
			&& (this.globalState.business?.portalSettings?.businessSettings?.enableMFA ?? false)
			&& (this.globalState.customer?.mfaSettings?.twoFactorEnabled ?? false))
		{
			this.showMfaHelpfulTextForSMS = ValidationHelpers.isPhoneNumber(ValidationHelpers.sanitizePhoneNumber(this.getMobilePhoneNumber));
			this.showMfaHelpfulTextForEmail = ValidationHelpers.isEmailAddress(this.getEmail);
		}
	}

	@computedFrom('model.mobilePhone')
	get getMobilePhoneNumber() {
		return this.model.mobilePhone;
	}

	@computedFrom('model.email')
	get getEmail() {
		return this.model.email;
	}

	@computedFrom('model.smsIsOptedIn', 'isSmsOptedIn')
	get isOptedInForSMS() {
		try {
			return this.model.smsIsOptedIn && this.isSmsOptedIn;
		}
		catch (err) {
			console.error('Error encounted identifying sms opt-in setting');
		}
	}

	getRouteSettings() {
		return {
			redirectTo: 'preferences',
			phoneNumber: this.model.mobilePhone,
			customerId: this.model.customer.id
		};
	}

	smsVerification() {
		this.router.currentInstruction.config.settings = this.getRouteSettings();
		this.router.navigateToRoute('verification');
	}

	updateContactInformation() {
		this.errorMessage = '';
		this.validationErrors = [];

		let notificationValidator = new NotificationValidator(this.i18n, this.isInvoiceEnabled);
		let mobileValidator = new MobileNumberValidator(this.i18n, this.isSmsOptedIn);

		if (notificationValidator.hasErrors(this.model) || mobileValidator.hasErrors(this.model)) {
			this.validationErrors = notificationValidator.errors.concat(mobileValidator.errors);
		} else {
			let contactInfo = new ContactInformation();
			contactInfo.customerId = this.model.customer.id;
			contactInfo.mobilePhone = this.model.mobilePhone;
			contactInfo.smsNotificationsEnabled = this.isSmsOptedIn;
			contactInfo.emailAddress = this.model.email;
			contactInfo.emailNotificationsEnabled = this.model.isEmailOptedIn;
			contactInfo.isPaperStatementsOptedIn = this.model.isPaperStatementsOptedIn;
			contactInfo.isEmailStatementsOptedIn = this.model.isEmailStatementsOptedIn;

			this.payrazrService.updateContactInformation(contactInfo)
				.then((response) => {
					if (response.isSuccessful) {
						this.setupMfaHelpfulText();
						this.successItem = this.i18n.i18next.t('personalInformation', 'Personal Information');
						if (response.entity) {
							this.smsRequiresVerification = true;
							this.router.currentInstruction.config.settings = this.getRouteSettings();
							this.router.navigateToRoute('verification');
						}
					}
					else {
						alert(response.message);
					}
				})
				.catch((err) => {
					this.errorMessage = this.i18n.i18next.t('updateUnsuccessfulError', { defaultValue: `Update was unsuccessful: ${err}`, error: err });
				});
		}
	}
}

// TODO: Refactor this class
export class Model {
	customer: any;
	username: any;
	currentPassword: any;
	newPassword: any;
	question1: any;
	question2: any;
	answer1: any;
	answer2: any;
	isEmailOptedIn: boolean;
	isPaperStatementsOptedIn: boolean;
	isEmailStatementsOptedIn: boolean;
	email: any;
	homePhone: any;
	mobilePhone: any;
	workPhone: any;
	smsIsOptedIn: boolean;

	constructor(customer) {
		this.customer = customer;

		this.username = customer.securitySettings.username;

		this.currentPassword = '';
		this.newPassword = '';

		this.question1 = customer.securitySettings.question1;
		this.question2 = customer.securitySettings.question2;

		this.answer1 = '';
		this.answer2 = '';

		this.isPaperStatementsOptedIn = customer.documentSettings.isPaperOptedIn;
		this.isEmailStatementsOptedIn = customer.documentSettings.isEmailOptedIn;

		this.email = customer.email;

		this.homePhone = customer.homePhone;
		this.mobilePhone = ValidationHelpers.sanitizePhoneNumber(customer.mobilePhone);
		this.workPhone = customer.workPhone;

		this.isEmailOptedIn = (customer.notificationSettings.isEmailOptedIn === undefined) ? false : customer.notificationSettings.isEmailOptedIn;
		// Unless the customer explicitly opts out, they are opted in.
		this.smsIsOptedIn = customer.notificationSettings.smsIsOptedIn === undefined ? true : customer.notificationSettings.smsIsOptedIn;
	}
}

export class MobileNumberValidator { 
	errors: any;
	smsOptedIn: any;
	phoneValidationRegex: any;

	constructor(private i18n: I18N, obj?) {
		this.errors = [];
		this.smsOptedIn = obj;
		this.phoneValidationRegex = /.*\d{3}.*\d{3}.*\d{4}/;
	}
	hasErrors(model) {
		if (this.smsOptedIn === true) {
			if (!model.mobilePhone) {
				this.addError(this.i18n.i18next.t('enterMobileNumber', 'Please enter in a mobile number'));
			}
			if (!this.phoneValidationRegex.test(model.mobilePhone)) {
				this.addError(this.i18n.i18next.t('mobileNumberDigitsVerification', 'Mobile number must be 10 digits'));
			}
		} else {
			// If SMS is not enabled continue to do basic validation on telephone number but allow empty field
			if (model.mobilePhone) {
				if (!this.phoneValidationRegex.test(model.mobilePhone)) {
					this.addError(this.i18n.i18next.t('mobileNumberDigitsVerification', 'Mobile number must be 10 digits'));
				}
			}
		}
		return this.errors.length > 0;
	}
	validNumber(model) {
		if (model.mobilePhone === '' || !this.phoneValidationRegex.test(model.mobilePhone)) {
			return false;
		}
		return true;

	}
	addError(e) {
		this.errors.push(e);
	}

}

export class NotificationValidator {
	errors: any;

	constructor(private i18n: I18N, private isInvoiceEnabled: boolean) {
		this.errors = [];
	}

	hasErrors(model: Model) {
		if ((model.isEmailOptedIn || model.isEmailStatementsOptedIn) && !ValidationHelpers.isEmailAddress(model.email)) {
			this.addError(this.i18n.i18next.t('provideValidEmail', 'Please provide a valid email address'));
		}

		if (this.isInvoiceEnabled && !model.isPaperStatementsOptedIn && !model.isEmailStatementsOptedIn) {
			this.addError(this.i18n.i18next.t('selectStatementNotificationMethod', 'Please select at least one statement notification method.'));
		}
		return this.errors.length > 0;
	}

	addError(e) {
		this.errors.push(e);
	}
}
